<script setup lang="ts">
import { UIButtonLink } from '@groover-dev/groover-ui'

import DatoSectionContainer from '~/components/dato/DatoSectionContainer.vue'

import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { provideFetchChartTags } from '~/api-core/Charts/FetchChartTags'

import type { ChartsFooterFragment } from '~/graphql/generated'

type Props = ChartsFooterFragment

defineProps<Props>()

const { coreFetch } = useProvideCoreFetch()

const { data } = await useLazyAsyncData(
  `GET_CHART_TAGS_SEO_FOOTER`,
  async () => {
    const getChartTags = provideFetchChartTags(coreFetch)
    return getChartTags()
  },
  {
    default: () => ({
      countries: [] as string[],
      genres: [] as string[],
    }),
    // prevent spamming this call since this gets called every time a genre/country is selected due to the route change
    // but we only need to fetch it once
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
  },
)

const sortedAvailableCountries = computed(() => {
  return data.value.countries.toSorted((a, b) => a.localeCompare(b))
})

const sortedAvailableGenres = computed(() => {
  return data.value.genres.toSorted((a, b) => a.localeCompare(b))
})
</script>

<template>
  <DatoSectionContainer>
    <div class="tw-relative tw-w-full">
      <div class="tw-mx-auto tw-max-w-3xl tw-text-center">
        <h3 v-if="title" class="tw-text-h3">{{ title }}</h3>
        <div v-if="subtitle" class="tw-mt-lg tw-text-base">{{ subtitle }}</div>
      </div>
      <div
        class="tw-mt-[56px] tw-grid tw-gap-x-sm tw-gap-y-5xl sm:tw-grid-cols-2"
      >
        <div class="tw-flex tw-flex-col tw-gap-2xl">
          <h5 class="tw-text-h5">{{ $t('charts.footer.countryLabel') }}</h5>
          <ul class="tw-grid tw-gap-3xl">
            <li v-for="country in sortedAvailableCountries" :key="country">
              <UIButtonLink
                size="small"
                color="neutral"
                :to="`/charts/${country}/`"
                >{{ $t(`charts.tags.country.${country}`) }}</UIButtonLink
              >
            </li>
          </ul>
        </div>
        <div class="tw-flex tw-flex-col tw-gap-2xl">
          <h5 class="tw-text-h5">{{ $t('charts.footer.genreLabel') }}</h5>
          <ul class="tw-grid tw-gap-3xl">
            <li v-for="genre in sortedAvailableGenres" :key="genre">
              <UIButtonLink
                size="small"
                color="neutral"
                :to="`/charts/all/${genre}/`"
                >{{ $t(`charts.tags.genre.${genre}`) }}</UIButtonLink
              >
            </li>
          </ul>
        </div>
      </div>
    </div>
  </DatoSectionContainer>
</template>

<style lang="scss" scoped>
.list-inner {
  @apply tw-grid tw-gap-3xl;
}
</style>
